import { useEffect, useRef, useState } from 'react';

type UseAppearanceDelayOptions = {
  minVisibleMs?: number;
  delayMs?: number;
};

/**
 * Хук задержки появления индикатора загрузки (например, спиннера).
 *
 * 💡 Поведение:
 * - Если загрузка длится меньше 300 мс — спиннер не показывается вовсе.
 * - Если загрузка длится дольше 300 мс — спиннер появляется.
 * - После появления спиннер остаётся на экране минимум 1000 мс, даже если загрузка завершилась раньше.
 *
 * Это предотвращает «мигание» спиннера и делает UI более плавным.
 *
 * @param {boolean} loading Состояние загрузки. Когда `true`, начинается отсчёт задержки.
 * @param {UseAppearanceDelayOptions} options Настройки таймингов
 * @returns {boolean} `true`, если спиннер (или другой индикатор) должен быть видимым.
 *
 * @example
 * const isSpinnerVisible = useAppearanceDelay(isLoading, { delayMs: 300, minVisibleMs: 1000 });
 * return isSpinnerVisible ? <InlineSpinner /> : null;
 */
export function useAppearanceDelay(
  loading: boolean,
  options: UseAppearanceDelayOptions = {}
): boolean {
  const { minVisibleMs = 1000, delayMs = 300 } = options;

  const [visible, setVisible] = useState<boolean>(false);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (loading) {
      if (showTimeoutRef.current || visible) {
        return;
      }

      showTimeoutRef.current = setTimeout(() => {
        setVisible(true);
        shownAtRef.current = Date.now();
        showTimeoutRef.current = null;
      }, delayMs);
    } else {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }

      if (visible) {
        const elapsed = shownAtRef.current
          ? Date.now() - shownAtRef.current
          : 0;
        const remaining = Math.max(minVisibleMs - elapsed, 0);

        hideTimeoutRef.current = setTimeout(() => {
          setVisible(false);
          hideTimeoutRef.current = null;
          shownAtRef.current = null;
        }, remaining);
      }
    }

    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }

      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [loading, visible]);

  return visible;
}
